import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import Select from 'react-select';
import TransactionsPanel from '../components/cashReserves/TransactionsPanel';
import { buildFamilies, fmtCurrencyPrecise, isFirmAccount } from '../components/cashReserves/helpers';
import TransactionSummaryCards from '../components/transactionHistory/TransactionSummaryCards';
import FamilyHoldingsHistoryChart from '../components/transactionHistory/FamilyHoldingsHistoryChart';
import cashReservesService from '../services/cashReservesService';
import '../styles/SelectStyles.css';

export const RANGE_OPTIONS = [
  { value: 'ytd', label: 'YTD' },
  { value: '3m', label: '3 Months' },
  { value: '6m', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: '2y', label: '2 Years' },
   { value: 'priorYear', label: 'Prior Year' },
  { value: 'custom', label: 'Custom' },
];

const EXPORT_COLUMNS = [
  { header: 'Trade Date', key: 'tradeDate', width: 14 },
  { header: 'Account', key: 'account', width: 28 },
  { header: 'Account Number', key: 'accountNumber', width: 16 },
  { header: 'Activity Type', key: 'activityType', width: 22 },
  { header: 'Symbol', key: 'symbol', width: 14 },
  { header: 'Amount', key: 'amount', width: 14 },
  { header: 'Reason Category', key: 'reasonCategory', width: 20 },
  { header: 'Note', key: 'note', width: 40 },
  { header: 'Linked Reserves', key: 'linkedReserves', width: 40 },
];

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_HEIGHT = 792;
const PDF_MARGIN = 48;
const PDF_CONTENT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
const PDF_COLORS = {
  text: [33, 37, 41],
  muted: [108, 117, 125],
  accent: [38, 93, 154],
  accentSoft: [245, 247, 249],
  rule: [222, 226, 230],
  noteBg: [255, 249, 232],
  noteRule: [255, 193, 7],
  cardBg: [250, 251, 252],
};
const PDF_EM_DASH = '\u2014';

const multiSelectStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: state.isFocused ? '#80bdff' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#80bdff' : '#adb5bd',
    },
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 10001,
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    zIndex: 10001,
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#e9ecef' : null,
    color: state.isSelected ? 'white' : 'black',
  }),
};

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fromIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addMonths = (date, months) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

const addYears = (date, years) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const toUtcDay = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

const dayDiffInclusive = (start, end) => {
  const diffMs = toUtcDay(end) - toUtcDay(start);
  return Math.floor(diffMs / 86400000) + 1;
};

const isManagementFeeTransaction = (tx) => {
  const haystack = [
    tx?.activity_type,
    tx?.symbol,
    tx?.description,
    tx?.memo,
    tx?.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /management\s*fee|mgmt\s*fee|advisory\s*fee/.test(haystack);
};

const OUTFLOW_KEYWORDS = [
  'withdrawal',
  'withdraw',
  'expense',
  'fee',
  'buy',
  'purchase',
  'distribution',
  'debit',
  'payment',
  'charge',
  'tax',
];

const INFLOW_KEYWORDS = [
  'deposit',
  'dividend',
  'interest',
  'sell',
  'sale',
  'proceeds',
  'contribution',
  'income',
  'credit',
  'receipt',
];

const activityDirection = (activityType) => {
  const text = String(activityType || '').trim().toLowerCase();
  if (!text) return null;
  if (OUTFLOW_KEYWORDS.some((keyword) => text.includes(keyword))) return -1;
  if (INFLOW_KEYWORDS.some((keyword) => text.includes(keyword))) return 1;
  return null;
};

const transactionCashImpact = (tx) => {
  const amount = Number(tx?.net_amount ?? tx?.amount ?? 0);
  if (!Number.isFinite(amount)) return 0;
  const direction = activityDirection(tx?.activity_type);
  if (direction == null) return amount;
  return direction * Math.abs(amount);
};

const rowIdentity = (row) => {
  const tx = row?.tx || {};
  const linkedReserves = Array.isArray(tx.linked_reserves)
    ? tx.linked_reserves
        .map((link) =>
          [
            String(link?.reserve_id ?? ''),
            String(link?.reserve_label ?? ''),
            String(link?.allocated_amount ?? ''),
          ].join('~')
        )
        .join(';')
    : '';
  return [
    String(row?.accountNumber || ''),
    String(tx.transaction_id ?? ''),
    String(tx.trade_date ?? ''),
    String(tx.activity_type ?? ''),
    String(tx.symbol ?? ''),
    String(tx.net_amount ?? tx.amount ?? ''),
    String(tx.user_note_reason_category ?? ''),
    String(tx.user_note ?? ''),
    linkedReserves,
  ].join('|');
};

const sameRows = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (rowIdentity(a[i]) !== rowIdentity(b[i])) return false;
  }
  return true;
};

const sameStringList = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (String(a[i] || '') !== String(b[i] || '')) return false;
  }
  return true;
};

const familyGroupKey = (account) => {
  if (account?.family_id) return account.family_id;
  const familyName = String(account?.family_name || '').trim();
  if (familyName) return `family-name:${familyName.toLowerCase()}`;
  return `acct:${account?.account_number || ''}`;
};

const formatLinkedReserves = (linkedReserves = []) =>
  linkedReserves
    .map((link) => {
      const label = link?.reserve_label || link?.reserve_id || 'reserve';
      return link?.allocated_amount != null
        ? `${label} (${fmtCurrencyPrecise(link.allocated_amount)})`
        : label;
    })
    .join('; ');

const sanitizeFilenamePart = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const buildExportFilenameBase = ({ familyName }) => {
  const familySlug = sanitizeFilenamePart(familyName) || 'family';
  return `transaction_history_${familySlug}_${new Date().toISOString().slice(0, 10)}`;
};

export const buildWindow = ({ rangeKey, customStart, customEnd }) => {
  const today = new Date();
  const todayIso = toIsoDate(today);

  if (rangeKey === 'priorYear') {
    const priorYear = today.getFullYear() - 1;
    const start = new Date(priorYear, 0, 1);
    const end = new Date(priorYear, 11, 31);
    return {
      start,
      end,
      days: dayDiffInclusive(start, end) - 1,
      asOf: toIsoDate(end),
      label: `${toIsoDate(start)} to ${toIsoDate(end)}`,
    };
  }

  if (rangeKey === 'custom') {
    const start = fromIsoDate(customStart);
    const end = fromIsoDate(customEnd);
    if (!start || !end) {
      return { error: 'Custom range requires both a start date and an end date.' };
    }
    if (start > end) {
      return { error: 'Custom range start date must be on or before end date.' };
    }
    return {
      start,
      end,
      days: dayDiffInclusive(start, end),
      asOf: toIsoDate(end),
      label: `${toIsoDate(start)} to ${toIsoDate(end)}`,
    };
  }

  const end = today;
  let start;
  if (rangeKey === 'ytd') start = new Date(today.getFullYear(), 0, 1);
  else if (rangeKey === '3m') start = addMonths(today, -3);
  else if (rangeKey === '6m') start = addMonths(today, -6);
  else if (rangeKey === '1y') start = addYears(today, -1);
  else if (rangeKey === '2y') start = addYears(today, -2);
  else start = addYears(today, -5);

  return {
    start,
    end,
    days: dayDiffInclusive(start, end),
    asOf: todayIso,
    label: `${toIsoDate(start)} to ${todayIso}`,
  };
};

export const buildTransactionExportRows = (rows = []) =>
  rows.map((row) => {
    const tx = row?.tx || {};
    const amount = Number(tx.net_amount ?? tx.amount ?? 0);
    return {
      tradeDate: tx.trade_date || '',
      account: row?.accountName || row?.accountNumber || '',
      accountNumber: row?.accountNumber || '',
      activityType: tx.activity_type || '',
      symbol: tx.symbol || '',
      amount: Number.isFinite(amount) ? amount : 0,
      amountDisplay: fmtCurrencyPrecise(Number.isFinite(amount) ? amount : 0),
      reasonCategory: tx.user_note_reason_category || '',
      note: tx.user_note || '',
      linkedReserves: formatLinkedReserves(tx.linked_reserves || []),
    };
  });

const escapeCsv = (value) => {
  const text = String(value ?? '');
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
};

const protectCsvFormula = (value) => {
  const text = String(value ?? '');
  return /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
};

export const buildTransactionsCsv = (rows = []) => {
  const header = EXPORT_COLUMNS.map((column) => column.header);
  const lines = [
    header.join(','),
    ...rows.map((row) =>
      [
        protectCsvFormula(row.tradeDate),
        protectCsvFormula(row.account),
        protectCsvFormula(row.accountNumber),
        protectCsvFormula(row.activityType),
        protectCsvFormula(row.symbol),
        row.amountDisplay,
        protectCsvFormula(row.reasonCategory),
        protectCsvFormula(row.note),
        protectCsvFormula(row.linkedReserves),
      ]
        .map(escapeCsv)
        .join(',')
    ),
  ];
  return `${lines.join('\n')}\n`;
};

export const buildTransactionsExcelBuffer = async (
  rows = [],
  { familyName = '', rangeLabel = '' } = {}
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Copilot';
  workbook.subject = [familyName, rangeLabel].filter(Boolean).join(' · ');
  const worksheet = workbook.addWorksheet('Transactions');
  worksheet.columns = EXPORT_COLUMNS;

  rows.forEach((row) => {
    worksheet.addRow({
      tradeDate: row.tradeDate,
      account: row.account,
      accountNumber: row.accountNumber,
      activityType: row.activityType,
      symbol: row.symbol,
      amount: row.amount,
      reasonCategory: row.reasonCategory,
      note: row.note,
      linkedReserves: row.linkedReserves,
    });
  });

  worksheet.getRow(1).font = { bold: true };
  worksheet.getColumn('amount').numFmt = '$#,##0.00;[Red]-$#,##0.00';
  return workbook.xlsx.writeBuffer();
};

const formatPdfValue = (value) => {
  const text = String(value ?? '').trim();
  return text || PDF_EM_DASH;
};

class TransactionPdfWriter {
  constructor(doc) {
    this.doc = doc;
    this.y = PDF_MARGIN;
  }

  ensureSpace(height) {
    if (this.y + height > PDF_PAGE_HEIGHT - PDF_MARGIN) {
      this.doc.addPage();
      this.y = PDF_MARGIN;
      return true;
    }
    return false;
  }

  setText(rgb) {
    this.doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  }

  setFill(rgb) {
    this.doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  }

  setDraw(rgb) {
    this.doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  }

  title(text) {
    this.ensureSpace(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    this.setText(PDF_COLORS.text);
    this.doc.text(text, PDF_MARGIN, this.y + 14);
    this.y += 22;
  }

  subtitle(text) {
    if (!text) return;
    this.ensureSpace(14);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.setText(PDF_COLORS.muted);
    this.doc.text(text, PDF_MARGIN, this.y + 10);
    this.y += 14;
  }

  rule() {
    this.ensureSpace(12);
    this.setDraw(PDF_COLORS.accent);
    this.doc.setLineWidth(1);
    this.doc.line(PDF_MARGIN, this.y + 4, PDF_MARGIN + PDF_CONTENT_WIDTH, this.y + 4);
    this.y += 12;
  }

  summary(rows, rangeLabel) {
    const totalAmount = rows.reduce(
      (sum, row) => sum + (Number.isFinite(row.amount) ? row.amount : 0),
      0
    );
    const metrics = [
      ['Transactions', String(rows.length)],
      ['Net Amount', fmtCurrencyPrecise(totalAmount)],
      ['Date Range', formatPdfValue(rangeLabel)],
    ];
    const rowHeight = 16;
    const boxHeight = metrics.length * rowHeight + 18;
    this.ensureSpace(boxHeight + 8);

    this.setFill(PDF_COLORS.accentSoft);
    this.setDraw(PDF_COLORS.rule);
    this.doc.setLineWidth(0.4);
    this.doc.roundedRect(PDF_MARGIN, this.y, PDF_CONTENT_WIDTH, boxHeight, 3, 3, 'FD');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.setText(PDF_COLORS.text);
    this.doc.text('Transaction Summary', PDF_MARGIN + 10, this.y + 14);

    metrics.forEach(([label, value], index) => {
      const rowY = this.y + 30 + index * rowHeight;
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(9);
      this.setText(PDF_COLORS.muted);
      this.doc.text(label, PDF_MARGIN + 10, rowY);

      this.doc.setFont('helvetica', 'normal');
      this.setText(PDF_COLORS.text);
      this.doc.text(value, PDF_MARGIN + 110, rowY);
    });

    this.y += boxHeight + 12;
  }

  transactionHeader(index, amountDisplay) {
    this.ensureSpace(24);
    this.setFill(PDF_COLORS.accentSoft);
    this.doc.roundedRect(PDF_MARGIN, this.y, PDF_CONTENT_WIDTH, 20, 3, 3, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.setText(PDF_COLORS.text);
    this.doc.text(`Transaction ${index}`, PDF_MARGIN + 10, this.y + 13);
    this.doc.text(formatPdfValue(amountDisplay), PDF_MARGIN + PDF_CONTENT_WIDTH - 10, this.y + 13, {
      align: 'right',
    });
    this.y += 24;
  }

  pairRow(leftLabel, leftValue, rightLabel, rightValue) {
    const gap = 16;
    const colWidth = (PDF_CONTENT_WIDTH - gap) / 2;
    const labelWidth = 90;
    const valueWidth = colWidth - labelWidth - 8;
    const leftLabelLines = this.doc.splitTextToSize(formatPdfValue(leftLabel), labelWidth);
    const leftValueLines = this.doc.splitTextToSize(formatPdfValue(leftValue), valueWidth);
    const rightLabelLines = this.doc.splitTextToSize(formatPdfValue(rightLabel), labelWidth);
    const rightValueLines = this.doc.splitTextToSize(formatPdfValue(rightValue), valueWidth);
    const lineCount = Math.max(
      leftLabelLines.length,
      leftValueLines.length,
      rightLabelLines.length,
      rightValueLines.length
    );
    const rowHeight = lineCount * 11 + 4;

    this.ensureSpace(rowHeight + 4);

    const renderColumn = (x, labelLines, valueLines) => {
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(8.5);
      this.setText(PDF_COLORS.muted);
      this.doc.text(labelLines, x, this.y + 9);

      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(9.5);
      this.setText(PDF_COLORS.text);
      this.doc.text(valueLines, x + labelWidth, this.y + 9);
    };

    renderColumn(PDF_MARGIN + 4, leftLabelLines, leftValueLines);
    renderColumn(PDF_MARGIN + 4 + colWidth + gap, rightLabelLines, rightValueLines);

    this.setDraw(PDF_COLORS.rule);
    this.doc.setLineWidth(0.3);
    this.doc.line(PDF_MARGIN, this.y + rowHeight, PDF_MARGIN + PDF_CONTENT_WIDTH, this.y + rowHeight);
    this.y += rowHeight;
  }

  singleRow(label, value) {
    const labelWidth = 110;
    const valueWidth = PDF_CONTENT_WIDTH - labelWidth - 8;
    const labelLines = this.doc.splitTextToSize(formatPdfValue(label), labelWidth);
    const valueLines = this.doc.splitTextToSize(formatPdfValue(value), valueWidth);
    const lineCount = Math.max(labelLines.length, valueLines.length);
    const rowHeight = lineCount * 11 + 4;

    this.ensureSpace(rowHeight + 4);

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8.5);
    this.setText(PDF_COLORS.muted);
    this.doc.text(labelLines, PDF_MARGIN + 4, this.y + 9);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9.5);
    this.setText(PDF_COLORS.text);
    this.doc.text(valueLines, PDF_MARGIN + 4 + labelWidth, this.y + 9);

    this.setDraw(PDF_COLORS.rule);
    this.doc.setLineWidth(0.3);
    this.doc.line(PDF_MARGIN, this.y + rowHeight, PDF_MARGIN + PDF_CONTENT_WIDTH, this.y + rowHeight);
    this.y += rowHeight;
  }

  textBlock(label, value) {
    const lines = this.doc.splitTextToSize(formatPdfValue(value), PDF_CONTENT_WIDTH - 20);
    const height = lines.length * 11 + 20;
    this.ensureSpace(height + 6);

    this.setFill(PDF_COLORS.noteBg);
    this.doc.roundedRect(PDF_MARGIN, this.y + 2, PDF_CONTENT_WIDTH, height, 3, 3, 'F');
    this.setFill(PDF_COLORS.noteRule);
    this.doc.rect(PDF_MARGIN, this.y + 2, 3, height, 'F');

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.setText(PDF_COLORS.text);
    this.doc.text(label, PDF_MARGIN + 8, this.y + 12);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9.5);
    this.doc.text(lines, PDF_MARGIN + 8, this.y + 24);
    this.y += height + 10;
  }

  emptyState(text) {
    const lines = this.doc.splitTextToSize(formatPdfValue(text), PDF_CONTENT_WIDTH - 20);
    const height = lines.length * 11 + 20;
    this.ensureSpace(height + 6);

    this.setFill(PDF_COLORS.cardBg);
    this.setDraw(PDF_COLORS.rule);
    this.doc.roundedRect(PDF_MARGIN, this.y, PDF_CONTENT_WIDTH, height, 3, 3, 'FD');
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.setText(PDF_COLORS.text);
    this.doc.text(lines, PDF_MARGIN + 10, this.y + 16);
    this.y += height + 10;
  }

  footer() {
    const pages = this.doc.internal.getNumberOfPages();
    for (let page = 1; page <= pages; page += 1) {
      this.doc.setPage(page);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.setText(PDF_COLORS.muted);
      this.doc.text(`Page ${page} of ${pages}`, PDF_PAGE_WIDTH - PDF_MARGIN, PDF_PAGE_HEIGHT - 20, {
        align: 'right',
      });
    }
  }
}

export const buildTransactionsPdfDoc = async (
  rows = [],
  { familyName = '', rangeLabel = '' } = {}
) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const writer = new TransactionPdfWriter(doc);

  writer.title('Transaction History');
  if (familyName) writer.subtitle(familyName);
  if (rangeLabel) writer.subtitle(rangeLabel);
  writer.subtitle(`Exported ${new Date().toLocaleString()}`);
  writer.rule();
  writer.summary(rows, rangeLabel);

  if (rows.length === 0) {
    writer.emptyState('No transactions matched the selected filters.');
  } else {
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      writer.transactionHeader(index + 1, row.amountDisplay);
      writer.pairRow('Trade Date', row.tradeDate, 'Activity Type', row.activityType);
      writer.pairRow('Account', row.account, 'Account Number', row.accountNumber);
      writer.pairRow('Symbol', row.symbol, 'Amount', row.amountDisplay);
      if (row.reasonCategory && row.linkedReserves) {
        writer.pairRow('Reason Category', row.reasonCategory, 'Linked Reserves', row.linkedReserves);
      } else if (row.reasonCategory) {
        writer.singleRow('Reason Category', row.reasonCategory);
      } else if (row.linkedReserves) {
        writer.singleRow('Linked Reserves', row.linkedReserves);
      }
      if (row.note) writer.textBlock('Note', row.note);
      writer.y += 2;
      if ((index + 1) % 25 === 0) {
        // Large histories can span hundreds of rows; yielding between batches keeps
        // the browser responsive without changing the generated document.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  writer.footer();
  return doc;
};

export const buildTransactionsPdfBlob = async (
  rows = [],
  { familyName = '', rangeLabel = '' } = {}
) => {
  const doc = await buildTransactionsPdfDoc(rows, { familyName, rangeLabel });
  return doc.output('blob');
};

const TransactionHistoryPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [error, setError] = useState('');

  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [rangeKey, setRangeKey] = useState('ytd');
  const todayIso = useMemo(() => toIsoDate(new Date()), []);
  const [customStart, setCustomStart] = useState(todayIso);
  const [customEnd, setCustomEnd] = useState(todayIso);

  const [selectedAccountNumbers, setSelectedAccountNumbers] = useState([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');

  const [activityTypes, setActivityTypes] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [holdingsHistory, setHoldingsHistory] = useState({ daily: [], events: [] });
  const [isLoadingHoldingsHistory, setIsLoadingHoldingsHistory] = useState(false);
  const [holdingsHistoryError, setHoldingsHistoryError] = useState('');
  const [exportingFormat, setExportingFormat] = useState('');
  const [lastLoadedExportScope, setLastLoadedExportScope] = useState('');

  const loadAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    setError('');
    try {
      const data = await cashReservesService.getReserveAccounts();
      const next = (data.accounts || []).filter((a) => !isFirmAccount(a));
      setAccounts(next);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load account families');
    } finally {
      setIsLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const families = useMemo(() => {
    const grouped = buildFamilies(
      accounts.map((account) => ({
        ...account,
        family_id: familyGroupKey(account),
      })),
      { grouped: true }
    );
    return grouped.sort((a, b) =>
      String(a.family_full_name || '').localeCompare(String(b.family_full_name || ''), undefined, {
        sensitivity: 'base',
      })
    );
  }, [accounts]);

  useEffect(() => {
    if (!selectedFamilyId) return;
    if (!families.some((f) => f.family_id === selectedFamilyId)) {
      setSelectedFamilyId('');
      setSelectedAccountNumbers([]);
    }
  }, [families, selectedFamilyId]);

  const selectedFamily = useMemo(
    () => families.find((family) => family.family_id === selectedFamilyId) || null,
    [families, selectedFamilyId]
  );

  const visibleAccounts = useMemo(() => {
    if (!selectedFamily) return [];
    if (selectedAccountNumbers.length === 0) return selectedFamily.accounts;
    const selectedSet = new Set(selectedAccountNumbers);
    return selectedFamily.accounts.filter((account) => selectedSet.has(account.account_number));
  }, [selectedFamily, selectedAccountNumbers]);

  const accountFilterOptions = useMemo(
    () =>
      (selectedFamily?.accounts || []).map((account) => ({
        value: account.account_number,
        label: account.account_name || account.account_number,
      })),
    [selectedFamily]
  );

  const selectedAccountOptions = useMemo(
    () => accountFilterOptions.filter((option) => selectedAccountNumbers.includes(option.value)),
    [accountFilterOptions, selectedAccountNumbers]
  );

  const activityTypeOptions = useMemo(
    () => activityTypes.map((activityType) => ({ value: activityType, label: activityType })),
    [activityTypes]
  );

  const selectedActivityTypeOptions = useMemo(
    () => activityTypeOptions.filter((option) => selectedActivityTypes.includes(option.value)),
    [activityTypeOptions, selectedActivityTypes]
  );

  const windowConfig = useMemo(
    () => buildWindow({ rangeKey, customStart, customEnd }),
    [rangeKey, customStart, customEnd]
  );

  const requestOptions = useMemo(() => {
    if (windowConfig.error) return null;
    return {
      days: windowConfig.days,
      as_of: windowConfig.asOf,
    };
  }, [windowConfig]);

  const cacheScope = useMemo(() => {
    if (!requestOptions) return '';
    return `${requestOptions.days}|${requestOptions.as_of || 'latest'}`;
  }, [requestOptions]);
  const exportScope = useMemo(
    () =>
      [
        selectedFamily?.family_id || '',
        cacheScope,
        selectedAccountNumbers.join(','),
        selectedActivityTypes.join(','),
        String(minAmountFilter || ''),
      ].join('|'),
    [selectedFamily, cacheScope, selectedAccountNumbers, selectedActivityTypes, minAmountFilter]
  );
  const isExportDataCurrent = exportScope !== '' && exportScope === lastLoadedExportScope;

  const summaryMetrics = useMemo(() => {
    const totals = {
      deposits: 0,
      withdrawals: 0,
      managementFees: 0,
      netTransactions: 0,
    };

    filteredRows.forEach((row) => {
      const tx = row.tx || {};
      const impact = transactionCashImpact(tx);
      if (impact >= 0) totals.deposits += impact;
      else totals.withdrawals += Math.abs(impact);
      if (isManagementFeeTransaction(tx)) totals.managementFees += Math.abs(impact);
      totals.netTransactions += impact;
    });

    return totals;
  }, [filteredRows]);

  const handleRowsChanged = useCallback(
    ({ filteredRows: rows = [], activityTypes: types = [], loadedCacheScope = '' }) => {
      setFilteredRows((prev) => (sameRows(prev, rows) ? prev : rows));
      setActivityTypes((prev) => (sameStringList(prev, types) ? prev : types));
      if (loadedCacheScope === cacheScope) {
        setLastLoadedExportScope(exportScope);
      }
    },
    [cacheScope, exportScope]
  );

  const exportRows = useMemo(() => buildTransactionExportRows(filteredRows), [filteredRows]);

  const handleExport = useCallback(
    async (format) => {
      if (!selectedFamily || exportRows.length === 0) return;
      setError('');
      setExportingFormat(format);
      const fileBase = buildExportFilenameBase({
        familyName: selectedFamily.family_full_name || selectedFamily.family_id,
      });
      try {
        if (format === 'csv') {
          const csv = buildTransactionsCsv(exportRows);
          downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${fileBase}.csv`);
        } else if (format === 'excel') {
          const buffer = await buildTransactionsExcelBuffer(exportRows, {
            familyName: selectedFamily.family_full_name,
            rangeLabel: windowConfig.label,
          });
          downloadBlob(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            `${fileBase}.xlsx`
          );
        } else if (format === 'pdf') {
          const blob = await buildTransactionsPdfBlob(exportRows, {
            familyName: selectedFamily.family_full_name,
            rangeLabel: windowConfig.label,
          });
          downloadBlob(blob, `${fileBase}.pdf`);
        }
      } catch (err) {
        setError(err.message || 'Failed to export transactions.');
      } finally {
        setExportingFormat('');
      }
    },
    [exportRows, selectedFamily, windowConfig.label]
  );

  const onFamilyChange = (nextFamilyId) => {
    setSelectedFamilyId(nextFamilyId);
    setSelectedAccountNumbers([]);
    setSelectedActivityTypes([]);
    setMinAmountFilter('');
    setFilteredRows([]);
    setActivityTypes([]);
  };

  const canRenderTransactions = Boolean(selectedFamily && requestOptions && !windowConfig.error);

  useEffect(() => {
    if (!selectedFamily || !windowConfig.start || !windowConfig.end || windowConfig.error) {
      setHoldingsHistory({ daily: [], events: [] });
      setHoldingsHistoryError('');
      setIsLoadingHoldingsHistory(false);
      return;
    }

    let cancelled = false;
    setIsLoadingHoldingsHistory(true);
    setHoldingsHistoryError('');

    cashReservesService
      .getFamilyHoldingsHistory(selectedFamily.family_id, {
        start_date: toIsoDate(windowConfig.start),
        end_date: toIsoDate(windowConfig.end),
        account_numbers: (selectedFamily.accounts || [])
          .map((account) => String(account.account_number || '').trim())
          .filter(Boolean)
          .join(','),
      })
      .then((response) => {
        if (cancelled) return;
        const daily = Array.isArray(response?.daily) ? response.daily : [];
        const events = Array.isArray(response?.events) ? response.events : [];
        const accountSet = new Set(visibleAccounts.map((a) => String(a.account_number || '')));

        const nextDaily = daily
          .map((point) => {
            const accounts = Array.isArray(point?.accounts) ? point.accounts : [];
            const filteredAccounts = accounts.filter((account) =>
              accountSet.has(String(account?.account_number || ''))
            );
            const filteredTotal = filteredAccounts.reduce(
              (sum, account) => sum + Number(account?.account_total_value || 0),
              0
            );
            const filteredCash = filteredAccounts.reduce(
              (sum, account) => sum + Number(account?.total_cash_dollar || 0),
              0
            );
            const accountValues = {};
            filteredAccounts.forEach((account) => {
              accountValues[String(account.account_number)] = Number(account.account_total_value || 0);
            });

            return {
              ...point,
              family_total_value: filteredTotal,
              family_total_cash: filteredCash,
              accounts: filteredAccounts,
              account_values: accountValues,
            };
          })
          .filter((point) => (point.accounts || []).length > 0);

        setHoldingsHistory({ daily: nextDaily, events });
      })
      .catch((err) => {
        if (cancelled) return;
        setHoldingsHistory({ daily: [], events: [] });
        setHoldingsHistoryError(
          err.response?.data?.detail || err.message || 'Failed to load holdings history.'
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHoldingsHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFamily, visibleAccounts, windowConfig]);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-0">
            <i className="bi bi-clock-history me-2 text-primary"></i>
            Transaction History
          </h3>
          <div className="text-muted small">
            Family-level transaction history with account, type, and amount filters.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-primary" onClick={loadAccounts} disabled={isLoadingAccounts}>
            <i className={`bi bi-arrow-clockwise me-1 ${isLoadingAccounts ? 'spin' : ''}`}></i>
            Refresh Families
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-4 align-items-stretch">
            <div className="col-xl-8 d-flex flex-column">
              <div className="row g-3 align-items-end">
                <div className="col-md-5">
                  <label className="form-label small text-uppercase text-muted" htmlFor="familySelect">
                    Account Family
                  </label>
                  <select
                    id="familySelect"
                    className="form-select"
                    value={selectedFamilyId}
                    onChange={(e) => onFamilyChange(e.target.value)}
                    disabled={isLoadingAccounts}
                  >
                    <option value="">Select an account family...</option>
                    {families.map((family) => (
                      <option key={family.family_id} value={family.family_id}>
                        {family.family_full_name} ({family.account_count})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label small text-uppercase text-muted" htmlFor="rangeSelect">
                    Date Range
                  </label>
                  <select
                    id="rangeSelect"
                    className="form-select"
                    value={rangeKey}
                    onChange={(e) => setRangeKey(e.target.value)}
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {rangeKey === 'custom' && (
                  <>
                    <div className="col-md-2">
                      <label className="form-label small text-uppercase text-muted" htmlFor="customStartDate">
                        Start Date
                      </label>
                      <input
                        id="customStartDate"
                        type="date"
                        className="form-control"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-uppercase text-muted" htmlFor="customEndDate">
                        End Date
                      </label>
                      <input
                        id="customEndDate"
                        type="date"
                        className="form-control"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>

              {windowConfig.error && (
                <div className="alert alert-warning py-2 px-3 mt-3 mb-0" role="alert">
                  {windowConfig.error}
                </div>
              )}

              {!windowConfig.error && selectedFamily && (
                <>
                  <div className="row g-3 align-items-end mt-2">
                    <div className="col-md-4">
                      <label className="form-label small text-uppercase text-muted" htmlFor="accountFilter">
                        Account Filter
                      </label>
                      <Select
                        inputId="accountFilter"
                        className="basic-single"
                        classNamePrefix="select"
                        isMulti
                        closeMenuOnSelect={false}
                        isSearchable
                        options={accountFilterOptions}
                        value={selectedAccountOptions}
                        onChange={(options) =>
                          setSelectedAccountNumbers((options || []).map((option) => option.value))
                        }
                        placeholder="All Accounts"
                        noOptionsMessage={() => 'No accounts available'}
                        menuPortalTarget={document.body}
                        styles={multiSelectStyles}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small text-uppercase text-muted" htmlFor="transactionTypeFilter">
                        Transaction Type
                      </label>
                      <Select
                        inputId="transactionTypeFilter"
                        className="basic-single"
                        classNamePrefix="select"
                        isMulti
                        closeMenuOnSelect={false}
                        isSearchable
                        options={activityTypeOptions}
                        value={selectedActivityTypeOptions}
                        onChange={(options) =>
                          setSelectedActivityTypes((options || []).map((option) => option.value))
                        }
                        placeholder="All Types"
                        noOptionsMessage={() => 'No transaction types available'}
                        menuPortalTarget={document.body}
                        styles={multiSelectStyles}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small text-uppercase text-muted" htmlFor="minAmountFilter">
                        Transactions Over Amount
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          id="minAmountFilter"
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          placeholder="No minimum"
                          value={minAmountFilter}
                          onChange={(e) => setMinAmountFilter(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {selectedFamily && !windowConfig.error && (
              <div className="col-xl-4 d-flex">
                <TransactionSummaryCards metrics={summaryMetrics} className="mb-0 h-100 flex-grow-1" />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedFamily && !windowConfig.error && (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex align-items-center gap-3 flex-nowrap overflow-auto">
              <span className="fw-semibold text-nowrap">
                <i className="bi bi-graph-up-arrow me-2 text-primary"></i>
                Family Holdings Trend
              </span>
              <span className="text-muted small text-nowrap ms-auto">
                Stacked holdings by account with deposit and withdrawal event markers.
              </span>
            </div>
            <div className="card-body">
              {isLoadingHoldingsHistory && (
                <div className="text-muted small py-3">Loading holdings history...</div>
              )}

              {!isLoadingHoldingsHistory && holdingsHistoryError && (
                <div className="alert alert-warning py-2 px-3 mb-0" role="alert">
                  {holdingsHistoryError}
                </div>
              )}

              {!isLoadingHoldingsHistory && !holdingsHistoryError && (
                <FamilyHoldingsHistoryChart
                  dailyPoints={holdingsHistory.daily}
                  events={holdingsHistory.events}
                  visibleAccounts={visibleAccounts}
                  familyFullName={selectedFamily.family_full_name}
                  selectedAllAccounts={selectedAccountNumbers.length === 0}
                />
              )}
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <span className="fw-semibold">
                  <i className="bi bi-table me-2 text-primary"></i>
                  Transactions ({filteredRows.length})
                </span>
              </div>
              <div className="d-flex align-items-center flex-wrap justify-content-end gap-2">
                <span className="text-muted small">
                 Use the note icon to add/edit/delete notes and link transactions to cash reserves.
                </span>
                <div className="btn-group" role="group" aria-label="Export transactions">
                 <button
                   type="button"
                   className="btn btn-sm btn-outline-primary"
                   onClick={() => handleExport('pdf')}
                   disabled={!isExportDataCurrent || filteredRows.length === 0 || Boolean(exportingFormat)}
                 >
                   <i className={`bi ${exportingFormat === 'pdf' ? 'bi-hourglass-split' : 'bi-file-earmark-pdf'} me-1`}></i>
                   Export PDF
                 </button>
                 <button
                   type="button"
                   className="btn btn-sm btn-outline-primary"
                   onClick={() => handleExport('csv')}
                   disabled={!isExportDataCurrent || filteredRows.length === 0 || Boolean(exportingFormat)}
                 >
                   <i className={`bi ${exportingFormat === 'csv' ? 'bi-hourglass-split' : 'bi-filetype-csv'} me-1`}></i>
                   Export CSV
                 </button>
                 <button
                   type="button"
                   className="btn btn-sm btn-outline-primary"
                   onClick={() => handleExport('excel')}
                   disabled={!isExportDataCurrent || filteredRows.length === 0 || Boolean(exportingFormat)}
                 >
                   <i className={`bi ${exportingFormat === 'excel' ? 'bi-hourglass-split' : 'bi-file-earmark-excel'} me-1`}></i>
                   Export Excel
                 </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {canRenderTransactions && (
                <TransactionsPanel
                  accounts={selectedFamily.accounts}
                  visibleAccounts={visibleAccounts}
                  days={requestOptions.days}
                  requestOptions={requestOptions}
                  cacheScope={cacheScope}
                  activityTypeFilter={selectedActivityTypes}
                  minAbsoluteAmount={minAmountFilter}
                  hideSummaryStrip
                  onRowsChanged={handleRowsChanged}
                />
              )}
            </div>
          </div>
        </>
      )}

      {!selectedFamily && !isLoadingAccounts && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-diagram-3" style={{ fontSize: '2rem' }}></i>
          <div className="mt-2">Select an account family to view transaction history.</div>
        </div>
      )}

      <style>{`
        .spin { animation: tx-history-spin 1s linear infinite; }
        @keyframes tx-history-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TransactionHistoryPage;
